// External packages
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// Types
import type { VisionModel } from '../../../server/src/types/media.types';

// Hooks
import { useToast } from 'hooks/useToast';
import { API_BASE_URL, authHeaders } from './base';

/**
 * Vision (YOLO) recognition models — one .tflite per place, served from the
 * CDN. The mobile apps discover them via GET /v2/vision-models/manifest
 * (authenticated with the organization's API key); the dashboard manages
 * them through the hooks below.
 */

export const MAX_MODEL_SIZE_BYTES = 100 * 1024 * 1024; // keep in sync with server/nginx

export const getVisionModels = async (): Promise<VisionModel[]> => {
  const response = await fetch(`${API_BASE_URL}/vision-models`, {
    method: 'GET',
    headers: authHeaders(),
    cache: 'no-cache',
  });

  if (!response.ok)
    throw new Error(`Failed to fetch vision models: ${response.statusText}`);

  const result = await response.json();
  return result.data as VisionModel[];
};

export const useGetVisionModels = () => {
  return useQuery({
    queryKey: ['vision-models'],
    queryFn: getVisionModels,
    initialData: [] as VisionModel[],
  });
};

/**
 * Upload with real progress reporting. Model files are 30+ MB, so a silent
 * `fetch` would leave the user staring at a spinner for a long time —
 * XMLHttpRequest exposes upload progress events, fetch does not.
 */
export const uploadVisionModel = (
  placeId: string,
  file: File,
  onProgress?: (fraction: number) => void,
  authToken: string = localStorage.getItem('authToken') || ''
): Promise<VisionModel> => {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', file);

    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_BASE_URL}/vision-models/upload/${placeId}`);
    xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress?.(event.loaded / event.total);
      }
    };

    xhr.onload = () => {
      try {
        const body = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300 && !body.error) {
          resolve(body.data as VisionModel);
        } else {
          reject(new Error(body.message || `Upload failed (${xhr.status})`));
        }
      } catch {
        reject(new Error(`Upload failed (${xhr.status})`));
      }
    };

    xhr.onerror = () => reject(new Error('Network error during model upload'));
    xhr.send(formData);
  });
};

export const useUploadVisionModel = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({
      placeId,
      file,
      onProgress,
    }: {
      placeId: string;
      file: File;
      onProgress?: (fraction: number) => void;
    }) => uploadVisionModel(placeId, file, onProgress),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vision-models'] });
      toast.success('Model je uspješno uploadan!');
    },
    onError: (error: any) => {
      console.error('Failed to upload vision model:', error);
      toast.error(error?.message || 'Greška pri uploadu modela.');
    },
  });
};

export const deleteVisionModel = async (
  id: string,
  authToken: string = localStorage.getItem('authToken') || ''
) => {
  const response = await fetch(`${API_BASE_URL}/vision-models/${id}`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
    cache: 'no-cache',
  });

  if (!response.ok)
    throw new Error(`Failed to delete vision model: ${response.statusText}`);

  return response.json();
};

export const useDeleteVisionModel = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (id: string) => deleteVisionModel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vision-models'] });
      toast.success('Model je uspješno obrisan!');
    },
    onError: (error: any) => {
      console.error('Failed to delete vision model:', error);
      toast.error(error?.message || 'Greška pri brisanju modela.');
    },
  });
};
